import React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { cn } from '../lib/utils';

// Placeholder (protected-segment) highlighting for translation output. bi-pepper masks
// non-translatable segments (URLs, wallet addresses, card numbers, promo codes, ...) behind
// [p_N] tokens while translating and restores them afterwards; its response lists every segment
// with rune offsets into the *request* text. Two rendering modes cover the two texts we show:
//   'offsets' — the request text itself (source), where the offsets can be used directly
//   'value'   — the translated text, where a segment is found by locating its restored value

// Segment type → token class, grouped for scannability: web identifiers blue, money/crypto green,
// codes and numbers gold.
const SEGMENT_TYPE_STYLES = {
  url: 'text-brand-blue',
  email: 'text-brand-blue',
  wallet: 'text-positive',
  iban: 'text-positive',
  txhash: 'text-positive',
  card: 'text-warning',
  phone: 'text-warning',
  promo: 'text-gold',
  number: 'text-gold',
};

const segmentClass = (type) => SEGMENT_TYPE_STYLES[type] || 'text-gold';

// runeOffsetsToUtf16 maps rune indices to UTF-16 code-unit indices for one text: offsets[r] is
// where rune r starts in text, offsets[runeCount] === text.length. bi-pepper's Start/End are rune
// offsets; JS string indices are UTF-16 code units, so slicing with the raw offsets would
// mis-highlight any text containing astral-plane characters (emoji, some symbols) before a segment.
const runeOffsetsToUtf16 = (text) => {
  const offsets = [0];
  let units = 0;
  for (const ch of text) {
    units += ch.length;
    offsets.push(units);
  }
  return offsets;
};

// segmentRanges resolves each segment to a [start, end) UTF-16 range in text, skipping the ones
// that cannot be located (out-of-range offsets, value not present, collision with an earlier
// match). Sorted by start so rendering is a single left-to-right pass.
const segmentRanges = (text, segments, mode) => {
  const ranges = [];
  if (mode === 'offsets') {
    const offsets = runeOffsetsToUtf16(text);
    const runeCount = offsets.length - 1;
    for (const seg of segments || []) {
      if (!Number.isInteger(seg.start) || !Number.isInteger(seg.end)) continue;
      if (seg.start < 0 || seg.end > runeCount || seg.start >= seg.end) continue;
      ranges.push({ start: offsets[seg.start], end: offsets[seg.end], seg });
    }
  } else {
    for (const seg of segments || []) {
      if (!seg.value) continue;
      const from = text.indexOf(seg.value);
      if (from < 0) continue;
      if (ranges.some((r) => from < r.end && r.start < from + seg.value.length)) continue;
      ranges.push({ start: from, end: from + seg.value.length, seg });
    }
  }
  return ranges.sort((a, b) => a.start - b.start);
};

// highlightSegments renders text with its protected segments colored and tooltip-annotated.
// Plain text (no segments, or none locatable) renders as-is.
export const highlightSegments = (text, segments, mode = 'offsets') => {
  if (!text) return text;
  const ranges = segmentRanges(text, segments, mode);
  if (ranges.length === 0) return text;

  const parts = [];
  let cursor = 0;
  ranges.forEach(({ start, end, seg }, i) => {
    if (start > cursor) parts.push(<React.Fragment key={`t${i}`}>{text.slice(cursor, start)}</React.Fragment>);
    parts.push(
      <Tooltip key={`s${i}`}>
        <TooltipTrigger asChild>
          <span
            className={cn('cursor-help underline decoration-dotted underline-offset-2', segmentClass(seg.type))}
          >
            {text.slice(start, end)}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px] break-all">
          <span className="font-bold">{seg.type}</span> — protected, not translated
        </TooltipContent>
      </Tooltip>,
    );
    cursor = end;
  });
  if (cursor < text.length) parts.push(<React.Fragment key="tail">{text.slice(cursor)}</React.Fragment>);
  return parts;
};

// missingSegmentValues returns the protected segments whose restored value is absent from text —
// the model dropped the placeholder and bi-pepper tolerantly skipped the restore. A dropped wallet
// address or promo code makes the message wrong, so this is worth a visible warning.
export const missingSegmentValues = (text, segments) =>
  (segments || []).filter((seg) => seg.value && !text.includes(seg.value));

// unionSegments merges segment lists from several results of the same case (one per run) into a
// single offset-based list for highlighting the source text: protection runs per translate call,
// and the source highlighting should show everything any run protected.
export const unionSegments = (segmentLists) => {
  const seen = new Set();
  const out = [];
  for (const segments of segmentLists || []) {
    for (const seg of segments || []) {
      const key = `${seg.start}:${seg.end}:${seg.value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(seg);
    }
  }
  return out;
};
